import React, { useState, useEffect } from 'react';
import { Star, Calendar, Check, ChevronRight, LogOut } from 'lucide-react';
import { BookingProgressIndicator } from './BookingProgressIndicator';
import { ServiceSelection } from './ServiceSelection';
import { DateTimeSelection } from './DateTimeSelection';
import { BookingConfirmation } from './BookingConfirmation';
import { BookingSuccess } from './BookingSuccess';
import { CancellationPolicy } from './CancellationPolicy';
import { CustomerBookingAPI } from '../../../services/api/customerBookingAPI';

export const BookingWizard = ({ customer, businessSlug, onComplete, onDisconnect }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  // Policy agreement state
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [showingPolicy, setShowingPolicy] = useState(false);

  const steps = [
    { title: 'שירות', icon: Star },
    { title: 'תאריך וזמן', icon: Calendar },
    { title: 'אישור', icon: Check }
  ];

  // Load services
  useEffect(() => {
    const loadServices = async () => {
      if (!businessSlug || !customer?.token) return;

      try {
        setServicesLoading(true);
        const customerServices = await CustomerBookingAPI.getCustomerServices(businessSlug, customer?.token);
        setServices(customerServices);
      } catch (error) {
        console.error('Error loading services:', error);
        setServices([]);
      } finally {
        setServicesLoading(false);
      }
    };

    loadServices();
  }, [businessSlug, customer?.token]);

  // Load available slots when service is selected
  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (!selectedService) {
        setAvailableSlots([]);
        return;
      }

      setLoadingSlots(true);
      try {
        const today = new Date();
        const baseDate = customStartDate ? new Date(customStartDate) : today;
        const startDate = customStartDate || today.toISOString().split('T')[0];

        const endDate = new Date(baseDate);
        endDate.setDate(baseDate.getDate() + 5);
        const endDateStr = endDate.toISOString().split('T')[0];

        const slots = await CustomerBookingAPI.getAvailableSlots(
          businessSlug,
          customer?.token,
          selectedService.id,
          startDate,
          endDateStr
        );

        const slotsWithAvailability = slots.filter((day) => day.totalSlots > 0);
        setAvailableSlots(slotsWithAvailability);
      } catch (error) {
        console.error('Error loading available slots:', error);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadAvailableSlots();
  }, [selectedService, customStartDate, businessSlug, customer?.token]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setCurrentStep(1);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 1) {
        setSelectedDate(null);
        setSelectedTimeSlot(null);
      } else if (currentStep === 2) {
        setSelectedTimeSlot(null);
      }
    }
  };

  const handleSelectSlot = (date, timeSlot) => {
    setSelectedDate(date);
    setSelectedTimeSlot(timeSlot);
  };

  const handleCustomDateChange = (e) => {
    setCustomStartDate(e.target.value);
  };

  const handleContinueToConfirmation = () => {
    setCurrentStep(2);
  };

  const handleShowPolicy = () => {
    setShowingPolicy(true);
  };

  const handleBackFromPolicy = () => {
    setShowingPolicy(false);
  };

  const handleConfirm = async () => {
    if (!selectedService || !selectedDate || !selectedTimeSlot) {
      console.error('Missing required booking information');
      return;
    }

    setIsBooking(true);
    setBookingError(null);

    try {
      await CustomerBookingAPI.createAppointment(
        businessSlug,
        {
          serviceId: selectedService.id,
          date: selectedDate,
          time: selectedTimeSlot.time,
          duration: selectedService.duration,
          price: selectedService.price,
          crewMemberId: selectedTimeSlot.crewMemberId,
          notes: `Appointment booked via customer portal for ${selectedService.name}`
        },
        customer?.token
      );

      console.log('Appointment created successfully');
      setIsConfirmed(true);
    } catch (error) {
      console.error('Error creating appointment:', error);
      setBookingError(error.message || 'שגיאה בקביעת התור. אנא נסה שוב.');
    } finally {
      setIsBooking(false);
    }
  };

  const resetBooking = () => {
    setCurrentStep(0);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setIsConfirmed(false);
    setCustomStartDate(null);
    setPolicyAgreed(false);
    setShowingPolicy(false);

    if (onComplete) {
      onComplete();
    }
  };

  if (isConfirmed) {
    return (
      <BookingSuccess
        service={selectedService}
        date={selectedDate}
        timeSlot={selectedTimeSlot}
        onComplete={resetBooking}
      />
    );
  }

  // Show cancellation policy inline when user clicks the link
  if (showingPolicy) {
    return (
      <CancellationPolicy
        onBack={handleBackFromPolicy}
        businessLogo={null}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-sm border-b border-white/20 sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            ) : (
              <button
                onClick={onDisconnect}
                className="flex items-center gap-1 text-red-600 hover:text-rose-500 transition-colors"
                title="התנתק"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-xs font-medium">התנתק</span>
              </button>
            )}
            <h1 className={`text-xl font-bold text-gray-900 ${currentStep === 0 ? 'text-center flex-1' : ''}`}>
              קבע תור
            </h1>
            {currentStep > 0 && <div className="w-10" />}
          </div>

          {/* Progress Steps */}
          <BookingProgressIndicator currentStep={currentStep} steps={steps} />
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        {/* Step 0: Service Selection */}
        {currentStep === 0 && (
          <ServiceSelection
            services={services}
            loading={servicesLoading}
            onSelectService={handleServiceSelect}
          />
        )}

        {/* Step 1: Date & Time Selection */}
        {currentStep === 1 && (
          <DateTimeSelection
            selectedService={selectedService}
            availableSlots={availableSlots}
            loading={loadingSlots}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            onSelectSlot={handleSelectSlot}
            customStartDate={customStartDate}
            onDateChange={handleCustomDateChange}
            onBack={handleBack}
            onContinue={handleContinueToConfirmation}
          />
        )}

        {/* Step 2: Confirmation */}
        {currentStep === 2 && (
          <BookingConfirmation
            service={selectedService}
            date={selectedDate}
            timeSlot={selectedTimeSlot}
            error={bookingError}
            onConfirm={handleConfirm}
            onBack={handleBack}
            isLoading={isBooking}
            policyAgreed={policyAgreed}
            onPolicyAgreedChange={setPolicyAgreed}
            onShowPolicy={handleShowPolicy}
          />
        )}
      </div>
    </div>
  );
};
